// Apolo IDE — GlobalCommandPalette (Sprint v8.3 — expanded from skill-only).
//
// ⌘K opens a global Spotlight-style palette over the current view.
// Categories with distinct colors:
//   1. Navegar       (purple)  — abrir tab Chat/Council/Code
//   2. Sessões       (cyan)    — saltar pras 8 sessões Code mais recentes
//   3. Skills        (blue)    — invocar /cmd no composer
//   4. Adapters      (emerald) — abrir Adapter sheet
//   5. Ações rápidas (amber)   — Nova sessão, Schedules, Plugins, etc.
//   6. GitHub        (gold)    — abrir profile externo
//
// Design: shad-pro cmdk + ui-ux-pro-max grouping + tastedesign color-coding
// + impeccable spring + microinteractions-mastery (dot glow per category,
// border-l-2 colored, hover lift, smooth scroll-into-view).

import { useEffect, useMemo, useState } from 'react';
import { Command as CmdkCommand } from 'cmdk';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  CalendarClock,
  Clock,
  Command,
  ExternalLink,
  Github,
  Layers,
  MessageSquare,
  Package,
  Plug,
  Plus,
  Search,
  Sparkles,
  Users,
  Zap,
} from 'lucide-react';
import { useSlashCommands } from '../hooks/useSlashCommands.js';
import { useAgentStore } from '../store/useAgentStore.js';
import { apiFetch } from '../lib/api.js';
import { pluginColor } from '../lib/pluginColor.js';

const SPRING = { type: 'spring', stiffness: 320, damping: 28 };
const RECENTS_KEY = 'apolo:plugin:skill:recents:v1';

const CATEGORIES = {
  navigation: { label: 'Navegar', color: '#a855f7', glow: 'rgba(168, 85, 247, 0.30)', icon: ArrowRight },
  session: { label: 'Sessões recentes', color: '#06b6d4', glow: 'rgba(6, 182, 212, 0.30)', icon: Clock },
  skill: { label: 'Skills', color: '#3b82f6', glow: 'rgba(59, 130, 246, 0.30)', icon: Command },
  adapter: { label: 'Adapters', color: '#10b981', glow: 'rgba(16, 185, 129, 0.30)', icon: Plug },
  action: { label: 'Ações rápidas', color: '#f59e0b', glow: 'rgba(245, 158, 11, 0.30)', icon: Zap },
  github: { label: 'GitHub', color: '#fbbf24', glow: 'rgba(251, 191, 36, 0.30)', icon: Github },
};

const NAV_ITEMS = [
  { id: 'nav:chat', label: 'Chat', sub: 'Conversa rápida com qualquer LLM', workspace: 'chat', icon: MessageSquare },
  { id: 'nav:council', label: 'Council', sub: 'Deliberação multi-agente em 3 fases', workspace: 'council', icon: Users },
  { id: 'nav:code', label: 'Code', sub: 'IDE com agentes + worktrees', workspace: 'code', icon: Command },
];

const ACTION_ITEMS = [
  { id: 'act:new-session', label: 'Novo chat', sub: 'Abrir composer · ⌘N', icon: Plus, event: 'apolo:newSession' },
  { id: 'act:schedules', label: 'Agenda', sub: 'Agendamentos · ⇧⌘T', icon: CalendarClock, event: 'apolo:openSchedule' },
  { id: 'act:plugins', label: 'Plugins & Skills', sub: 'Catalog completo · ⇧⌘K', icon: Package, event: 'apolo:openPlugins' },
  { id: 'act:adapters', label: 'Adapters', sub: 'Configurar LLM keys · ⇧⌘E', icon: Layers, event: 'apolo:openAdapters' },
];

const GITHUB_ITEMS = [
  { id: 'gh:profile', label: 'Abrir GitHub profile', sub: 'github.com/pedromleal15', icon: Github, url: 'https://github.com/pedromleal15' },
  { id: 'gh:contributions', label: 'Minhas contribuições', sub: 'Heatmap last year · authenticated', icon: ExternalLink, url: 'https://github.com/pedromleal15?tab=overview' },
];

function recordRecent(cmd) {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const existing = Array.isArray(arr) ? arr : [];
    const filtered = existing.filter((r) => r.skill_id !== cmd.id);
    const next = [
      {
        skill_id: cmd.id,
        slash_command: cmd.slash_command,
        display_name: cmd.display_name,
        last_used_at: Date.now(),
      },
      ...filtered,
    ].slice(0, 20);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* localStorage quota or JSON failure — silent */
  }
}

function dispatchInsert(cmd) {
  window.dispatchEvent(
    new CustomEvent('apolo:insertComposer', {
      detail: { text: cmd.slash_command + ' ' },
    }),
  );
}

function CategoryHeading({ category }) {
  const cat = CATEGORIES[category];
  return (
    <div className="flex items-center gap-2 px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: cat.color }}>
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: cat.color, boxShadow: `0 0 6px ${cat.glow}` }}
      />
      {cat.label}
    </div>
  );
}

function ItemRow({ category, icon: Icon, label, sub, badge, onSelect, searchValue, accent, swatch = false }) {
  const cat = CATEGORIES[category];
  // `accent` overrides the category color per-row (skills are colored by their
  // plugin/namespace). `swatch` drops the glyph for a pure color chip — color
  // becomes the identifier, no icon.
  const color = accent || cat.color;
  return (
    <CmdkCommand.Item
      value={searchValue}
      onSelect={onSelect}
      className={
        'group relative mb-1 flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-apolo-textSecondary transition-all duration-150 last:mb-0 ' +
        'data-[selected=true]:bg-white/[0.06] data-[selected=true]:text-apolo-textPrimary ' +
        'motion-reduce:transition-none'
      }
      style={{
        // Left bar in the accent/category color, brighter when selected.
        boxShadow: `inset 2px 0 0 0 ${color}55`,
      }}
    >
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-all duration-150 group-data-[selected=true]:scale-110"
        style={{
          backgroundColor: swatch ? color : `${color}18`,
          color: swatch ? '#ffffff' : color,
          boxShadow: `0 0 0 1px ${color}30`,
        }}
      >
        {swatch ? null : <Icon size={13} aria-hidden="true" />}
      </span>
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-[13px] font-medium">{label}</span>
        {sub && <span className="truncate text-[11px] text-apolo-textTertiary">{sub}</span>}
      </div>
      {badge && (
        <span
          className="ml-auto rounded px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider"
          style={{ color, backgroundColor: `${color}12` }}
        >
          {badge}
        </span>
      )}
      <ArrowRight
        size={11}
        aria-hidden="true"
        className="ml-1 shrink-0 text-apolo-textTertiary opacity-0 transition-opacity group-data-[selected=true]:opacity-100"
      />
    </CmdkCommand.Item>
  );
}

export default function SkillCommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [adapters, setAdapters] = useState([]);
  const { commands, loading } = useSlashCommands();

  const sessions = useAgentStore((s) => s.code.sessions);
  const setActiveWorkspace = useAgentStore((s) => s.setActiveWorkspace);
  const setActiveSession = useAgentStore((s) => s.setActiveSession);

  useEffect(() => {
    function onOpen() {
      setOpen(true);
    }
    window.addEventListener('apolo:openSkillPalette', onOpen);
    return () => window.removeEventListener('apolo:openSkillPalette', onOpen);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (open && e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Carrega adapters sob demanda — primeira abertura.
  useEffect(() => {
    if (!open || adapters.length > 0) return;
    apiFetch('/api/adapters')
      .then((res) => {
        if (res.ok && Array.isArray(res.data?.adapters)) setAdapters(res.data.adapters);
      })
      .catch(() => {});
  }, [open, adapters.length]);

  const recentSessions = useMemo(() => sessions.slice(0, 8), [sessions]);
  // harness-library-uplift E2 — filtrar pela busca ANTES do corte de 40: o cmdk só
  // filtra itens RENDERIZADOS, então cortar primeiro deixava 48 das 88 skills
  // inalcançáveis. Sem busca o comportamento é idêntico (top-40).
  const skillsList = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pool = q
      ? commands.filter((c) =>
          `${c.slash_command} ${c.display_name} ${c.plugin_slug || ''} ${c.description || ''}`
            .toLowerCase()
            .includes(q))
      : commands;
    return pool.slice(0, 40);
  }, [commands, search]);
  const adaptersList = useMemo(() => adapters.slice(0, 12), [adapters]);

  function handleNav(item) {
    setActiveWorkspace(item.workspace);
    setOpen(false);
    setSearch('');
  }

  function handleAction(item) {
    window.dispatchEvent(new CustomEvent(item.event));
    setOpen(false);
    setSearch('');
  }

  function handleSession(s) {
    setActiveSession(s.id);
    setActiveWorkspace('code');
    setOpen(false);
    setSearch('');
  }

  function handleSkill(cmd) {
    recordRecent(cmd);
    dispatchInsert(cmd);
    setOpen(false);
    setSearch('');
  }

  function handleAdapter(adapter) {
    // Dispatch event that AdapterView can listen pra abrir o sheet específico.
    window.dispatchEvent(
      new CustomEvent('apolo:openAdapter', { detail: { adapterId: adapter.id } }),
    );
    setOpen(false);
    setSearch('');
  }

  function handleGithub(item) {
    window.open(item.url, '_blank', 'noopener,noreferrer');
    setOpen(false);
    setSearch('');
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-50 bg-apolo-overlay backdrop-blur-md"
            aria-hidden="true"
          />
          {/* Wrapper flex pra centralizar — evita conflito entre
              Tailwind -translate-x-1/2 e Framer Motion transform animation
              (Framer sobrescreve transform). */}
          <div className="pointer-events-none fixed inset-0 z-50 flex items-start justify-center pt-[14vh] px-4">
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Command Palette"
            initial={{ scale: 0.96, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 12 }}
            transition={SPRING}
            className="pointer-events-auto w-full max-w-[44rem] overflow-hidden rounded-[22px] border border-white/10"
            style={{
              background: 'rgba(18,18,20,0.72)',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              boxShadow:
                '0 24px 70px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)',
            }}
          >
            <CmdkCommand label="Command palette" className="flex flex-col">
              <div className="flex items-center gap-2.5 border-b border-white/[0.06] px-4 py-3.5">
                <Search size={15} aria-hidden="true" className="text-apolo-textTertiary" />
                <CmdkCommand.Input
                  autoFocus
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Buscar tudo — tabs, sessões, skills, adapters, ações…"
                  className="flex-1 bg-transparent text-[15px] text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                />
                <kbd className="rounded bg-apolo-highlightMuted px-2 py-0.5 text-[10px] font-medium text-apolo-highlight">
                  ⌘K
                </kbd>
              </div>
              <CmdkCommand.List className="max-h-[520px] overflow-y-auto p-1.5">
                {loading && adapters.length === 0 && commands.length === 0 ? (
                  <div className="px-3 py-8 text-center text-sm text-apolo-textTertiary">
                    Carregando catálogo…
                  </div>
                ) : (
                  <>
                    <CmdkCommand.Empty className="flex flex-col items-center gap-1.5 px-3 py-10 text-center text-sm text-apolo-textTertiary">
                      <Sparkles size={16} aria-hidden="true" />
                      Nada encontrado pra &ldquo;{search}&rdquo;.
                    </CmdkCommand.Empty>

                    {/* NAVEGAÇÃO */}
                    <CmdkCommand.Group>
                      <CategoryHeading category="navigation" />
                      {NAV_ITEMS.map((item) => (
                        <ItemRow
                          key={item.id}
                          category="navigation"
                          icon={item.icon}
                          label={`Ir para ${item.label}`}
                          sub={item.sub}
                          badge={item.workspace}
                          searchValue={`${item.label} ${item.sub} navegar tab ${item.workspace}`}
                          onSelect={() => handleNav(item)}
                        />
                      ))}
                    </CmdkCommand.Group>

                    {/* SESSÕES RECENTES */}
                    {recentSessions.length > 0 && (
                      <CmdkCommand.Group>
                        <CategoryHeading category="session" />
                        {recentSessions.map((s) => (
                          <ItemRow
                            key={`sess-${s.id}`}
                            category="session"
                            icon={Clock}
                            label={s.name || 'Sessão sem nome'}
                            sub={`${s.branch || 'main'} · ${s.status || 'idle'}`}
                            searchValue={`sessao ${s.name} ${s.branch || ''} ${s.status || ''}`}
                            onSelect={() => handleSession(s)}
                          />
                        ))}
                      </CmdkCommand.Group>
                    )}

                    {/* SKILLS */}
                    {skillsList.length > 0 && (
                      <CmdkCommand.Group>
                        <CategoryHeading category="skill" />
                        {skillsList.map((cmd) => (
                          <ItemRow
                            key={`skill-${cmd.id || cmd.slash_command}`}
                            category="skill"
                            icon={Sparkles}
                            accent={pluginColor(cmd.plugin_slug)}
                            label={cmd.display_name}
                            sub={`${cmd.slash_command} · ${cmd.plugin_display_name || cmd.plugin_slug}`}
                            badge={cmd.handler_type?.replace('_', ' ')}
                            searchValue={`skill ${cmd.slash_command} ${cmd.display_name} ${cmd.plugin_slug || ''} ${cmd.description || ''}`}
                            onSelect={() => handleSkill(cmd)}
                          />
                        ))}
                      </CmdkCommand.Group>
                    )}

                    {/* ADAPTERS */}
                    {adaptersList.length > 0 && (
                      <CmdkCommand.Group>
                        <CategoryHeading category="adapter" />
                        {adaptersList.map((a) => (
                          <ItemRow
                            key={`adapter-${a.id}`}
                            category="adapter"
                            icon={Plug}
                            label={a.display_name}
                            sub={`${a.kind} · ${a.has_api_key ? 'conectado' : 'sem key'}`}
                            badge={a.is_active ? 'on' : 'off'}
                            searchValue={`adapter ${a.name} ${a.display_name} ${a.kind} ${a.description || ''}`}
                            onSelect={() => handleAdapter(a)}
                          />
                        ))}
                      </CmdkCommand.Group>
                    )}

                    {/* AÇÕES */}
                    <CmdkCommand.Group>
                      <CategoryHeading category="action" />
                      {ACTION_ITEMS.map((item) => (
                        <ItemRow
                          key={item.id}
                          category="action"
                          icon={item.icon}
                          label={item.label}
                          sub={item.sub}
                          searchValue={`acao ${item.label} ${item.sub}`}
                          onSelect={() => handleAction(item)}
                        />
                      ))}
                    </CmdkCommand.Group>

                    {/* GITHUB */}
                    <CmdkCommand.Group>
                      <CategoryHeading category="github" />
                      {GITHUB_ITEMS.map((item) => (
                        <ItemRow
                          key={item.id}
                          category="github"
                          icon={item.icon}
                          label={item.label}
                          sub={item.sub}
                          searchValue={`github ${item.label} ${item.sub}`}
                          onSelect={() => handleGithub(item)}
                        />
                      ))}
                    </CmdkCommand.Group>
                  </>
                )}
              </CmdkCommand.List>
              <div className="flex items-center justify-between border-t border-white/[0.06] bg-white/[0.02] px-4 py-2 text-[10px] text-apolo-textTertiary">
                <span className="inline-flex items-center gap-2">
                  <Sparkles size={10} aria-hidden="true" className="text-apolo-accent" />
                  Spotlight global · busca em tudo
                </span>
                <span className="inline-flex items-center gap-2">
                  <kbd className="rounded bg-apolo-surface3 px-1 py-0.5">↑↓</kbd>
                  navegar
                  <kbd className="rounded bg-apolo-surface3 px-1 py-0.5">↵</kbd>
                  selecionar
                  <kbd className="rounded bg-apolo-surface3 px-1 py-0.5">esc</kbd>
                  fechar
                </span>
              </div>
            </CmdkCommand>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
